// src/services/sessionSnapshot.ts
//
// Turns a live agent session into the small, safe blob that goes to durable
// storage, and back again. Both directions are pure functions over plain data
// so they can be exercised without a session manager, a network, or Word.
//
// Two properties this module exists to guarantee:
//
//  1. The blob stays small. A ChatMessage carries the full document OOXML at
//     the time it was sent - 0.5 to 5 MB each, read by nothing after the
//     request goes out - and a playbook body runs to hundreds of thousands of
//     characters. Neither is written.
//
//  2. The blob restores to a resting UI. Every in-flight flag is cleared, so a
//     session saved while a spinner was on screen does not come back with a
//     spinner that can never resolve.
//
// Attachment metadata IS written, deliberately: the files themselves cannot be
// restored (their bytes live in a webview Word destroys on close), so the names
// are the only way a reopened conversation can account for them instead of
// appearing to have invented them.
//
// The per-tool top-level fields on ChatMessage (autoRedlineOutputs,
// playbookResult, catylexSearchResults, ...) are deliberately not written.
// They duplicate inlineToolEvents by reference, which doubles them under
// JSON.stringify, and they are only a render fallback for messages that
// predate inlineToolEvents - the live path writes both, and apply-time
// mutations land on inlineToolEvents alone, so the copies drift.

import {
  AgentSessionSnapshot,
  ChatMessage,
  InlineToolEvent,
  MessageRole,
  PersistedChatMessage,
  PersistedSessionPayloadV1,
  UserMessageAction,
} from "../interfaces/chatSession";
import { NormalizedPlaybookResult } from "../interfaces/playbookTypes";
import { createLogger } from "../utils/logger";
import { track } from "../utils/analytics";

const log = createLogger("SessionSnapshot");

/**
 * Version stamped onto every payload this build writes. Bump it when the shape
 * changes incompatibly; `rehydrateSessionPayload` refuses anything it does not
 * recognise rather than guessing at the older shape.
 */
export const PERSISTED_SESSION_SCHEMA_VERSION = 1;

const VALID_ROLES: ReadonlySet<string> = new Set<MessageRole>(["user", "assistant", "system"]);

/**
 * Strip the playbook body while keeping everything the collapsed card renders
 * from. `playbook_omitted` is what tells the viewer to offer a reload rather
 * than treat the empty body as "no playbook".
 */
function prunePlaybookResult(result: NormalizedPlaybookResult): NormalizedPlaybookResult {
  return {
    ...result,
    playbook: "",
    playbook_omitted: true,
  };
}

/**
 * Clear the in-flight flags on one inline tool event and drop the playbook
 * body. Flags are only rewritten when the key is actually present, so a pruned
 * event never grows keys the live one did not have.
 */
function pruneInlineToolEvent(event: InlineToolEvent): InlineToolEvent {
  const data: InlineToolEvent["data"] = { ...event.data };

  if (data.isProcessing !== undefined) {
    data.isProcessing = false;
  }
  if (data.isWebSearching !== undefined) {
    data.isWebSearching = false;
  }
  if (data.isWebFetching !== undefined) {
    data.isWebFetching = false;
  }
  if (data.thinking) {
    // The signature must survive: a thinking block without one is dropped when
    // the conversation history is rebuilt for the model.
    data.thinking = { ...data.thinking, isStreaming: false };
  }
  if (data.playbookResult) {
    data.playbookResult = prunePlaybookResult(data.playbookResult);
  }
  if (data.addComment?.status === "applying") {
    // Applying a comment is a post-turn action, so it is not covered by the
    // rule that nothing is saved mid-stream. Storing the in-flight value would
    // restore a card spinning on work that is no longer running; the comment
    // either landed before the save, or it is genuinely still to be applied.
    data.addComment = { ...data.addComment, status: "pending" };
  }

  return { ...event, data };
}

/**
 * Reduce one message to the fields that are worth storing.
 *
 * The picks are explicit rather than a spread-and-delete: a field added to
 * ChatMessage later is then dropped by default, which is the safe direction.
 */
function pruneMessage(message: ChatMessage): PersistedChatMessage {
  const persisted: PersistedChatMessage = {
    id: message.id,
    role: message.role,
    // Verbatim - the `position` on every inline tool event is a character
    // offset into this string, so any trimming or normalizing here would
    // misplace every card in the message.
    content: message.content,
    timestamp: message.timestamp,
  };

  // What the bubble shows for an action's message, and what tells a restored
  // conversation which route it is on.
  if (message.action) {
    persisted.action = message.action;
  }
  if (message.inlineToolEvents) {
    persisted.inlineToolEvents = message.inlineToolEvents.map(pruneInlineToolEvent);
  }
  if (message.documentCitations) {
    persisted.documentCitations = message.documentCitations;
  }
  if (message.formRequestPayload) {
    persisted.formRequestPayload = message.formRequestPayload;
  }
  if (message.formSubmitted !== undefined) {
    persisted.formSubmitted = message.formSubmitted;
  }
  if (message.formSubmittedData) {
    persisted.formSubmittedData = message.formSubmittedData;
  }
  if (message.composerModeActiveOnCreation !== undefined) {
    persisted.composerModeActiveOnCreation = message.composerModeActiveOnCreation;
  }
  if (message.autoRedlineApplied !== undefined) {
    persisted.autoRedlineApplied = message.autoRedlineApplied;
  }
  if (message.autoRedlineApplyIndex !== undefined) {
    persisted.autoRedlineApplyIndex = message.autoRedlineApplyIndex;
  }
  if (message.staleApplyAttempts) {
    persisted.staleApplyAttempts = message.staleApplyAttempts;
  }
  if (message.wasInterrupted !== undefined) {
    persisted.wasInterrupted = message.wasInterrupted;
  }
  // Names only, and tiny. The files themselves cannot be restored, so this is
  // what lets a reopened conversation say which ones it was working from.
  if (message.attachments?.length) {
    persisted.attachments = message.attachments;
  }
  // Kept even though the cards themselves render from inlineToolEvents: these
  // feed the URL-to-title map behind citation hover tooltips, which reads the
  // message fields directly. Dropping them left restored citations untitled.
  if (message.webSearchEvents) {
    persisted.webSearchEvents = message.webSearchEvents;
  }
  if (message.webFetchEvents) {
    persisted.webFetchEvents = message.webFetchEvents;
  }
  if (message.restoredFromPersistence) {
    // Once restored, always restored: a message that came back from storage
    // must keep its automation suppression through every later save.
    persisted.restoredFromPersistence = true;
  }

  return persisted;
}

/**
 * Ceiling on a stored session, kept below the backend's own 5 MB limit.
 *
 * Without a cap a long-running conversation eventually crosses that limit, and
 * because an oversized payload is rejected rather than retried, persistence for
 * that agent would stop for good the first time it happened. Dropping the
 * oldest turns keeps the recent conversation restorable instead: losing the
 * start of a very long history is a far smaller loss than silently losing
 * everything from that point on.
 */
const MAX_PERSISTED_PAYLOAD_BYTES = 4 * 1024 * 1024;

/** Serialized size of a value, measured the way the request body will be. */
function serializedSize(value: unknown): number {
  return JSON.stringify(value)?.length ?? 0;
}

/**
 * Build the blob to store for one agent's session.
 *
 * @param snapshot Live session state, as handed out by the session manager
 * @returns The payload to send as the opaque `payload` of a session envelope
 */
export function buildPersistedPayload(snapshot: AgentSessionSnapshot): PersistedSessionPayloadV1 {
  const payload: PersistedSessionPayloadV1 = {
    schemaVersion: PERSISTED_SESSION_SCHEMA_VERSION,
    seenTipIds: [...snapshot.seenTipIds],
    messages: snapshot.messages.map(pruneMessage),
  };

  if (snapshot.selectedPlaybook) {
    payload.selectedPlaybook = snapshot.selectedPlaybook;
  }

  // The send path creates an empty assistant bubble before generation starts,
  // and reading a large document can take long enough for a debounce to fire in
  // that gap. Storing it would restore a blank bubble the model never wrote.
  const last = payload.messages[payload.messages.length - 1];
  if (
    last &&
    last.role === "assistant" &&
    !last.content &&
    !last.inlineToolEvents?.length &&
    !last.wasInterrupted
  ) {
    payload.messages.pop();
  }

  // Trim from the front until it fits. The check is on the whole payload
  // because that is what the size limit applies to.
  let dropped = 0;
  while (payload.messages.length > 1 && serializedSize(payload) > MAX_PERSISTED_PAYLOAD_BYTES) {
    payload.messages.shift();
    dropped++;
  }
  if (dropped > 0) {
    log.warn("Dropped the oldest messages to keep the stored session within its size limit", {
      dropped,
      remaining: payload.messages.length,
    });
    // Reported rather than only logged: the browser console is unreachable in
    // production, and this is the early warning that conversations are growing
    // into the size ceiling.
    track("session:payload_trimmed", { dropped, remaining: payload.messages.length });
  }

  return payload;
}

/**
 * Shape check for one stored message. Anything that fails is skipped rather
 * than restored half-formed - a message without an id or a numeric timestamp
 * would break keying and ordering in the message list.
 */
function isRestorableMessage(candidate: unknown): candidate is PersistedChatMessage {
  if (!candidate || typeof candidate !== "object") {
    return false;
  }
  const message = candidate as Record<string, unknown>;
  if (
    typeof message.id !== "string" ||
    typeof message.role !== "string" ||
    !VALID_ROLES.has(message.role) ||
    typeof message.content !== "string" ||
    typeof message.timestamp !== "number"
  ) {
    return false;
  }

  // inlineToolEvents is spread and sorted during render, so a value of the
  // wrong shape throws inside React rather than here. That is not recoverable
  // in the pane: the boundary asks the user to reload, and the reload restores
  // the same payload. Anything that is not a well-formed array of positioned
  // events is rejected here instead, which costs one message rather than the
  // whole conversation.
  if (message.inlineToolEvents !== undefined) {
    if (!Array.isArray(message.inlineToolEvents)) return false;
    if (!message.inlineToolEvents.every(isRestorableInlineToolEvent)) return false;
  }

  return true;
}

/**
 * Every action a stored message may carry. Typed against the union so that
 * adding an action without listing it here fails to compile rather than
 * silently stripping it from every restored conversation.
 */
const KNOWN_USER_MESSAGE_ACTIONS: Record<UserMessageAction, true> = {
  quick_analysis: true,
  draft_certificate: true,
};

function isUserMessageAction(value: unknown): value is UserMessageAction {
  return typeof value === "string" && value in KNOWN_USER_MESSAGE_ACTIONS;
}

/** Shape check for one stored inline tool event. */
function isRestorableInlineToolEvent(candidate: unknown): boolean {
  if (!candidate || typeof candidate !== "object") return false;
  const event = candidate as Record<string, unknown>;
  return (
    typeof event.id === "string" &&
    typeof event.type === "string" &&
    typeof event.position === "number" &&
    !!event.data &&
    typeof event.data === "object"
  );
}

/**
 * Read a stored blob back into the shape the session manager hydrates from.
 *
 * Never throws: storage is best-effort, so anything unrecognised collapses to
 * null and the agent simply starts with an empty conversation. The dropped
 * per-tool top-level fields are deliberately NOT reconstructed - inlineToolEvents
 * is the render and history source, and synthesizing the legacy copies would
 * only re-create the divergence they are dropped for.
 *
 * @param payload The opaque payload from a session envelope
 * @returns Session state ready to hydrate, or null if it cannot be used
 */
export function rehydrateSessionPayload(payload: unknown): AgentSessionSnapshot | null {
  if (!payload || typeof payload !== "object") {
    log.warn("Stored session payload is not an object - ignoring");
    return null;
  }

  const stored = payload as Partial<PersistedSessionPayloadV1>;

  if (stored.schemaVersion !== PERSISTED_SESSION_SCHEMA_VERSION) {
    log.warn("Stored session payload has an unsupported schema version - ignoring", {
      schemaVersion: stored.schemaVersion,
      supported: PERSISTED_SESSION_SCHEMA_VERSION,
    });
    return null;
  }

  const rawMessages = Array.isArray(stored.messages) ? stored.messages : [];
  const messages: ChatMessage[] = [];
  let skipped = 0;

  for (const raw of rawMessages) {
    if (!isRestorableMessage(raw)) {
      skipped++;
      continue;
    }
    // An action this build does not know (written by a newer one) is dropped
    // on its own: the message is still a good message, it just renders as the
    // text it carries rather than as the action that sent it.
    const { action, ...rest } = raw;
    const restored: ChatMessage = isUserMessageAction(action) ? { ...rest, action } : rest;
    // The marker is what keeps unattended, document-mutating automation from
    // firing against whatever document happens to be open now.
    messages.push({ ...restored, restoredFromPersistence: true });
  }

  if (skipped > 0) {
    log.warn("Skipped malformed messages while restoring a session", { skipped });
  }

  // TODO: attached files are not persisted. Graph-backed sources (SharePoint,
  // Document Library, Outlook) carry a stable id, so once those attachments
  // reach clients their references can be stored here and re-derived by id on
  // restore. Locally picked files cannot be, and would have to be re-attached.

  return {
    // contractType is deliberately not persisted. It is derived from the
    // document by classification, and a restored conversation is not tied to a
    // document - reopening on a different contract would otherwise send the
    // previous document's type as a prompt hint and an analytics dimension for
    // the rest of the launch, with no classification run left to correct it.
    contractType: undefined,
    selectedPlaybook: stored.selectedPlaybook ?? null,
    seenTipIds: Array.isArray(stored.seenTipIds)
      ? stored.seenTipIds.filter((id): id is string => typeof id === "string")
      : [],
    messages,
  };
}
